"use client";

import { useEffect, useRef } from "react";

const ownerTestimonials = [
  {
    name: "Sandra K.",
    role: "Airbnb Superhost · 3BR Condo",
    text: "Revenue up 30% in 3 months. The turnovers are immaculate.",
    metric: "+30% revenue",
  },
  {
    name: "Marcus D.",
    role: "Out-of-State Investor · 3 Properties",
    text: "I live in Atlanta and they handle everything. Completely hands-off.",
    metric: "3 properties",
  },
  {
    name: "Jennifer T.",
    role: "VRBO Host · 4BR Gulf-Front",
    text: "Went from 4.6 to 4.98 stars in just 3 months.",
    metric: "4.6 → 4.98 ⭐",
  },
];

const occupancyStats = [
  { label: "PCB Avg. Occupancy", value: "72%", sub: "Year-round average", icon: "📊" },
  { label: "Peak Season (Jun–Aug)", value: "91%", sub: "Summer occupancy rate", icon: "☀️" },
  { label: "Premier Client Avg.", value: "78%", sub: "Above market average", icon: "🏆" },
  { label: "Avg. Nightly Rate PCB", value: "$285", sub: "2024 market data", icon: "💵" },
];

export default function ProofModules() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef?.current?.querySelectorAll(".reveal-hidden");
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="section-padding bg-sand">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center mb-14 reveal-hidden">
          <span className="text-gold text-xs font-bold uppercase tracking-widest block mb-3">
            Social Proof
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl text-navy mb-4">
            Trusted by{" "}
            <span className="italic font-light text-gold">PCB Owners</span>
          </h2>
          <p className="text-navy/60 text-lg max-w-xl mx-auto">
            Real ratings, real results, real PCB market data.
          </p>
        </div>

        {/* Owner Testimonial Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12 reveal-hidden">
          {ownerTestimonials?.map((t, i) => (
            <div
              key={i}
              className="bg-navy rounded-2xl p-6 relative overflow-hidden group hover:scale-[1.02] transition-transform duration-300"
            >
              <div className="absolute top-0 right-0 w-24 h-24 rounded-full bg-gold/10 blur-2xl pointer-events-none" />
              <div className="relative z-10">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)]?.map((_, j) => (
                    <svg key={j} className="w-3.5 h-3.5 text-gold fill-gold" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-white/80 text-sm leading-relaxed italic mb-5">&ldquo;{t.text}&rdquo;</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-semibold text-sm">{t.name}</p>
                    <p className="text-white/40 text-xs">{t.role}</p>
                  </div>
                  <span className="bg-gold/20 border border-gold/30 text-gold text-xs font-bold px-3 py-1 rounded-full">
                    {t.metric}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* PCB Occupancy Stats */}
        <div className="bg-gradient-to-r from-navy to-navy-dark rounded-3xl p-8 sm:p-10 reveal-hidden">
          <div className="text-center mb-8">
            <p className="text-gold text-xs font-bold uppercase tracking-widest mb-2">PCB Market Data</p>
            <h3 className="font-heading text-2xl sm:text-3xl text-white">
              Panama City Beach Occupancy Statistics
            </h3>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {occupancyStats?.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl mb-2">{stat.icon}</div>
                <p className="font-heading text-3xl sm:text-4xl text-gold font-bold mb-1">{stat.value}</p>
                <p className="text-white font-medium text-sm mb-0.5">{stat.label}</p>
                <p className="text-white/40 text-xs">{stat.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
